import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { usableAccountQrTenant } from '../../../shared/tenant/customerApiTenant.js';
import {
  useCustomerOrderDetail,
  useCustomerOrderPickupQr,
  useCustomerSelfCollectOrder,
} from '../../orders/ordersApi.js';
import { useBuyAgainFlow } from '../useBuyAgainFlow.js';
import { resolveUnifiedOrdersLoadMessageKey } from '../presenters/unifiedOrdersLoadError.js';

export type AccountOrderDetailScreenPhase =
  | 'invalid'
  | 'loading'
  | 'error'
  | 'empty'
  | 'ready';

export function useAccountOrderDetailScreen(transactionIdParam: string | undefined) {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const location = useLocation();
  const id = Number(transactionIdParam);
  const detailQuery = useCustomerOrderDetail(Number.isFinite(id) ? id : -1);
  const selfCollectMutation = useCustomerSelfCollectOrder();
  const buyAgain = useBuyAgainFlow();

  const data = detailQuery.data;
  const pickupQr = useCustomerOrderPickupQr(
    id,
    Number.isFinite(id) &&
      data !== undefined &&
      data.paymentRequired === false &&
      data.pickupQrVisible === true,
  );

  let phase: AccountOrderDetailScreenPhase = 'loading';
  if (!Number.isFinite(id)) {
    phase = 'invalid';
  } else if (detailQuery.isLoading) {
    phase = 'loading';
  } else if (detailQuery.error instanceof Error) {
    phase = 'error';
  } else if (!data) {
    phase = 'empty';
  } else {
    phase = 'ready';
  }

  // G2 — never invent `__platform__` for QR reopen.
  // paymentsHref uses only the order-row org slug (omit ?tenant= when missing).
  // Self-collect may still use usable chrome via rowTenantCode.
  const usableOrderTenant = usableAccountQrTenant(data?.tenantCode ?? '');
  const usableChromeTenant = usableAccountQrTenant(tenantCode);
  const rowTenant = usableOrderTenant ?? usableChromeTenant ?? '';
  const qrTenant = usableOrderTenant;

  // Wave D chrome SSOT — always bare `/account` (never `/{tenant}/account`).
  const accountBase = '/account';
  let paymentsHref = `${accountBase}/orders`;
  if (data?.paymentId !== null && data?.paymentId !== undefined) {
    const qrPath = `${accountBase}/payments/${encodeURIComponent(data.paymentId)}/qr`;
    paymentsHref =
      qrTenant !== null
        ? `${qrPath}?tenant=${encodeURIComponent(qrTenant)}`
        : qrPath;
  }
  return {
    t,
    tenantCode,
    rowTenantCode: rowTenant,
    location,
    phase,
    id,
    data,
    errorMessage:
      detailQuery.error instanceof Error
        ? t(resolveUnifiedOrdersLoadMessageKey(detailQuery.error))
        : '',
    pickupQr,
    selfCollectMutation,
    buyAgain,
    paymentsHref,
  };
}
